import React, { createRef, useEffect, useState } from "react";
import { SectionContainer, SectionContainerWrapper } from "../../library/utilities/SectionContainer";
import styled from "styled-components";
import { breakpointRuleOverDesktop, breakpointRuleTablet, ColorGrayscale } from "../../library/utilities/variables";
import { ButtonBlock, ButtonBlockOutline, ButtonLink } from "../../library/symbols/buttons";
import { BodyS, FontFamilyInter, HeadlinesH1 } from "../../library/utilities/fonts";
import { ItineraryPresentation } from "../components/ItineraryPage/ItineraryPresentation";
import { AnimatePresence, motion } from "framer-motion";
import { ShareMediaModal } from "../components/ShareMediaModal";
const ItineraryPageContainer = styled(SectionContainer) `
    position: relative;
    z-index: 0;
`;
const ItineraryPageBackground = styled.div.attrs({ 'aria-hidden': true }) `
    z-index: -1;
    pointer-events: none;

    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    margin-top: -48px;

    & > .background-svg {
        display: block;
        width: 100%;
        height: 100%;

        svg {
            width: 100%;
            height: auto;

            * {
                fill: var(--level-primary-color);
            }
        }

        &.background-svg--desktop {
            display: none;

            svg {
                width: auto;
                height: 100%;
            }
        }
    }

    @media (${breakpointRuleTablet}) {
        & > .background-svg {
            &.background-svg--mobile {
                display: none;
            }

            &.background-svg--desktop {
                display: block;
            }
        }

        margin-top: -88px;
    }

    @media (${breakpointRuleOverDesktop}) {
        & > .background-svg {
            &.background-svg--desktop {
                svg {
                    width: 100%;
                    height: auto;
                }
            }
        }
    }
`;
const ItineraryPageWrapper = styled(SectionContainerWrapper) `
    margin-top: 32px;
    margin-bottom: 32px;
    padding: 48px 24px;
    max-width: 790px;

    background: ${ColorGrayscale.white};
    border: 2px solid var(--level-primary-color);
    box-shadow: 0 4px 0 var(--level-primary-color);
    border-radius: 24px;

    @media (${breakpointRuleTablet}) {
        padding: 56px 50px 54px;
        margin-bottom: 100px;
    }
`;
const ItineraryCategory = styled.h2 `
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 1px;

    text-transform: uppercase;
    text-align: center;
    margin-bottom: 12px;

    @media (${breakpointRuleTablet}) {
        margin-bottom: 24px;
    }
`;
const ItineraryTitle = styled(HeadlinesH1) `
    margin-bottom: 12px;
    text-align: center;

    @media (${breakpointRuleTablet}) {
        margin-bottom: 24px;
    }
`;
const ItineraryDescription = styled.p `
    ${FontFamilyInter};
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
    letter-spacing: 0;
    text-align: center;

    margin-bottom: 48px;

    @media (${breakpointRuleTablet}) {
        padding: 0 100px;
        margin-bottom: 58px;
    }
`;
const CourseListTitle = styled.h3 `
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0;
    text-align: center;

    margin-bottom: 24px;
`;
const CourseList = styled.ul `
    margin: 0 auto 32px;
    max-width: 581px;

    @media (${breakpointRuleTablet}) {
        margin: 0 auto 48px;
    }
`;
const CourseListItem = styled(BodyS).attrs({ as: 'li' }) `
    text-align: center;
    padding: 15px 0;

    border-top: 2px solid rgba(229, 229, 229, 0.5);

    &:last-of-type {
        border-bottom: 2px solid rgba(229, 229, 229, 0.5);
    }

    .CourseListItemIcon {
        margin-right: 8px;
    }
`;
const CourseListItemIcon = () => {
    return (React.createElement("svg", { className: "CourseListItemIcon", width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M2.66602 8.20914L6.43725 11.9804L13.9797 4.4379", stroke: "#1670E4", strokeWidth: "2", strokeLinecap: "round" })));
};
const CTAWrapper = styled.div `
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 48px;

    ${ButtonBlock} {
        margin-bottom: 12px;
    }

    @media (${breakpointRuleTablet}) {
        margin-bottom: 50px;

        ${ButtonBlock} {
            max-width: 345px;
        }
    }
`;
const CTATitle = styled.p `
    ${FontFamilyInter};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 26px;
    letter-spacing: 0;
    text-align: center;

    margin-bottom: 12px;

    @media (${breakpointRuleTablet}) {
        margin-bottom: 14px;
    }

`;
const CTAButtonBlock = styled(ButtonBlock).attrs({ as: 'a' }) ``;
const MiscLinksWrapper = styled.div `
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;

    padding-top: 18px;
    border-top: 2px solid rgba(229, 229, 229, 0.5);

    ${ButtonLink} {
        margin-bottom: 38px;

        &:last-of-type {
            margin-bottom: 0;
        }
    }

    @media (${breakpointRuleTablet}) {
        flex-direction: row;

        justify-content: center;
        padding-top: 32px;

        ${ButtonLink} {
            margin-right: 115px;
            margin-bottom: 0;

            &:last-of-type {
                margin-right: 0;
            }
        }
    }
`;
export function ItineraryPage({ itineraryNode, itinerary, links }) {
    const ItineraryPresentationDuration = 1.5;
    const [presentationFinished, setPresentationFinished] = useState(false);
    const [shareMediaModal, setShareMediaModal] = useState(false);
    const backgroundMobileRef = createRef();
    const backgroundDesktopRef = createRef();
    useEffect(() => {
        setTimeout(() => {
            setPresentationFinished(true);
        }, (ItineraryPresentationDuration * 2) * 1000);
    }, []);
    useEffect(() => {
        if (itinerary && presentationFinished) {
            document.documentElement.style.setProperty('--level-primary-color', itinerary.level.primary_color);
            document.documentElement.style.setProperty('--level-secondary-color', itinerary.level.secondary_color);
            document.documentElement.style.setProperty('--level-ternary-color', ColorGrayscale.black);
            const desktopBackground = new DOMParser()
                .parseFromString(itinerary.category.test_result_desktop, "image/svg+xml");
            const mobileBackground = new DOMParser()
                .parseFromString(itinerary.category.test_result_mobile, "image/svg+xml");
            backgroundMobileRef.current.append(mobileBackground.querySelector('svg'));
            backgroundDesktopRef.current.append(desktopBackground.querySelector('svg'));
        }
    }, [itinerary, presentationFinished]);
    return (React.createElement(AnimatePresence, { presenceAffectsLayout: true, exitBeforeEnter: true },
        itinerary && presentationFinished && (React.createElement(React.Fragment, null,
            React.createElement(ItineraryPageContainer, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
                React.createElement(ItineraryPageWrapper, null,
                    React.createElement(ItineraryCategory, null, itinerary.category.name),
                    React.createElement(ItineraryTitle, null, itinerary.title),
                    React.createElement(ItineraryDescription, null, itinerary.description),
                    React.createElement(CourseListTitle, null, "Incluye estos cursos:"),
                    React.createElement(CourseList, null, itinerary.required_courses.map((course) => (React.createElement(CourseListItem, { key: course.id },
                        React.createElement(CourseListItemIcon, null),
                        course.title)))),
                    React.createElement(CTAWrapper, null,
                        React.createElement(CTATitle, null, "Es gratis y 100% online"),
                        React.createElement(CTAButtonBlock, { href: itinerary.url }, "Ver el itinerario"),
                        React.createElement(ButtonBlockOutline, { onClick: () => setShareMediaModal(true) }, "Compartir")),
                    React.createElement(MiscLinksWrapper, null,
                        React.createElement(ButtonLink, { "$link": true, href: links.itineraries_page }, "Ver otros itinerarios"),
                        React.createElement(ButtonLink, { "$link": true, href: links.quiz_page }, "Hacer nuevamente el test"))),
                React.createElement(ItineraryPageBackground, null,
                    React.createElement("span", { className: "background-svg background-svg--mobile", ref: backgroundMobileRef }),
                    React.createElement("span", { className: "background-svg background-svg--desktop", ref: backgroundDesktopRef }))),
            React.createElement(ShareMediaModal, { itinerary: itinerary, isOpen: shareMediaModal, onClose: () => setShareMediaModal(false) }))),
        (!itinerary || !presentationFinished) && (React.createElement(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } },
            React.createElement(ItineraryPresentation, { duration: ItineraryPresentationDuration })))));
}
